/**
 * The world — one deterministic simulation composed from the physics
 * subsystems, each of which was written and tested in isolation.
 *
 * Nothing new is modelled here. This file decides ORDER and OWNERSHIP, which is
 * where an assembly of independently correct subsystems actually goes wrong: a
 * force applied by two modules is applied twice, a timer stepped by two modules
 * runs at double rate, and a rotation integrated by two modules turns the car
 * twice as fast. All three of those are real, were flagged by the subsystem
 * authors, and are resolved below.
 *
 * ===========================================================================
 * TICK ORDER
 * ===========================================================================
 *
 *   0. match.step(dt)
 *        Phase timers and the clock. Runs first so that a tick is unambiguously
 *        either frozen or live, decided before anything moves.
 *
 *   1. FROZEN SHORT-CIRCUIT
 *        While `match.frozen` (countdown, goal celebration, replay) no car
 *        system runs at all. In particular `stepJump` does not see the inputs,
 *        so its button-edge detector stays armed: a player who holds jump
 *        through the countdown gets exactly one jump the instant play starts,
 *        not zero.
 *
 *        WHICH POSE IS HELD depends on the phase, and the distinction is the
 *        whole goal moment. A COUNTDOWN holds the kickoff pose. The goal
 *        celebration and the replay hold the SCORING pose — the field exactly
 *        as the goal left it, ball in the net — because a celebration that has
 *        already snapped the ball back to centre never shows anybody the goal
 *        they just scored. See `holdFrozenPose` and `freezeScoringPose`.
 *
 *   2. boostPads.step(cars, dt)
 *        Respawn timers, then pickups. Before the cars move, so a pad is
 *        collected from the position the player actually drove onto last tick
 *        and the boost is available to THIS tick's thrust. Running it after the
 *        cars move would make pickup depend on a position the player never saw.
 *
 *   3. PER CAR, in car order (skipping demolished cars):
 *
 *      3a. stepJump(car, input, dt) -> jumpStep
 *          The jump/dodge state machine, reading `car.onGround` as left by the
 *          PREVIOUS tick's ground step. That staleness is deliberate and is
 *          RocketSim's own structure: its `_PreTickUpdate` runs the jump, boost
 *          and air-torque passes against the wheel-contact count Bullet
 *          computed at the end of the previous step. Running the jump BEFORE
 *          the suspension is also what makes conflict 4 go away — see below.
 *
 *      3b. stepGroundControl(car, input, arena, dt, {
 *              applyBoostThrust: false,      // conflict 1 — ./boost owns thrust
 *              updateSupersonicFlag: false,  // conflict 2 — ./boost owns the flag
 *              integrateOrientationFlag: false, // conflict 3 — we own rotation
 *          })
 *          Suspension raycasts, surface transitions, driving forces, gravity,
 *          linear integration, and the angular-VELOCITY contributions from the
 *          springs and from steering. This is the only step that moves a car.
 *
 *      3c. ROTATION, exactly once:
 *              airborne && jumpStep.airControl
 *                  -> stepAerialRotation (aerial torque + integrate)
 *              otherwise
 *                  -> integrateOrientation (integrate only)
 *          `airborne` is the FRESH `!car.onGround` from 3b, so a car that lands
 *          this tick does not also receive an air-control torque on top of the
 *          landing alignment.
 *
 *      3d. stepCarArenaContacts, then containCar — see "CAR CONTACT" below.
 *
 *      3e. stepBoost(car, input, dt)
 *          Burst state, tank accounting, thrust, and `updateSupersonic`. Last,
 *          so the supersonic flag is computed from the car's FINAL velocity for
 *          the tick (conflict 2). Because 3b has already integrated position,
 *          thrust added here reaches the position on the next tick; the
 *          difference is BOOST_ACCELERATION * dt^2 = 0.07 uu per tick and it is
 *          the price of having the flag see a final velocity.
 *
 *   4. CAR vs CAR, in two halves:
 *
 *      4a. stepRespawns(cars, dt)
 *          Respawn timers only. First, so a car demolished on tick T is not
 *          charged for the tick it died on.
 *
 *      4b. stepCarCarCollisions(cars, dt)
 *          Every overlapping pair, tested on this tick's positions: the
 *          rigid-body response (depenetration + impulses, friction 0.09 /
 *          restitution 0.1), the scripted BUMP impulses, and the demolitions.
 *          One pass, because in RocketSim it is one Bullet callback — the same
 *          front-bumper test decides whether a contact bumps or kills, and the
 *          same per-victim cooldown suppresses both. See ../physics/car-car.
 *
 *          It runs AFTER every car has been stepped and resolved against the
 *          arena, so both cars are at their final poses for the tick when the
 *          pair is tested. `containCar` is re-run on the cars afterwards
 *          because depenetrating a pair moves cars that the arena pass had
 *          already placed.
 *
 *   5. BALL
 *        integrate -> arena contacts -> car-ball contacts -> arena contacts
 *        again. The trailing arena pass exists because a car can push the ball
 *        into a wall on the same tick; without it the ball can be left
 *        overlapping the shell with an outward velocity, which is how a ball
 *        escapes an arena over a long run.
 *
 *   6. GOAL DETECTION
 *        On the post-collision ball position, so a ball that is pushed back out
 *        of the goal mouth by the frame in the same tick never scores.
 *
 * ===========================================================================
 * THE FOUR OWNERSHIP CONFLICTS
 * ===========================================================================
 *
 * 1. BOOST THRUST — ./boost owns it; ./ground is told `applyBoostThrust: false`.
 *    ./ground can only see `input.boost`, but BOOST_MIN_TIME keeps a burst (and
 *    therefore the thrust) alive for 0.1 s after the button is released, and
 *    a burst also ends the instant the tank runs dry mid-tick. Only ./boost
 *    knows either. With both modules enabled a boosting car accelerated at
 *    2 * BOOST_ACCELERATION; `test/sim/world.test.ts` asserts the single rate to
 *    1 %.
 *
 * 2. SUPERSONIC — ./boost owns it; ./ground is told `updateSupersonicFlag:
 *    false`. The two implementations are the same function, so running both was
 *    harmless for the FLAG and fatal for `supersonicTimer`, which advanced by
 *    2*dt and so halved the 1 s maintain window. ./boost wins because it is the
 *    last thing to touch the car's velocity, and the flag has to describe the
 *    velocity the tick ends with — the demolition rule keys off the flag.
 *
 * 3. ORIENTATION — the world owns it, unconditionally, once per car per tick.
 *    The subsystems each own a piece of angular VELOCITY (springs and steering
 *    in ./ground, dodge torque in ./jump, air control in ./aerial) and none of
 *    them may advance the matrix. ./ground grew an `integrateOrientationFlag`
 *    for this. A case analysis would not have been enough: with one or two
 *    wheels touching, ./ground integrated while `stepJump` still reported air
 *    control, so that band double-integrated even though neither module was
 *    wrong on its own.
 *
 * 4. JUMP vs SUSPENSION — running `stepJump` before `stepGroundControl` is what
 *    makes a jump take. The impulse is on the velocity before the suspension
 *    raycasts, so the springs see a wheel moving AWAY from the surface, take
 *    their relaxation-damping branch, and clamp to zero force; the only thing
 *    left opposing the jump is the 325 uu/s^2 sticky force, which removes 2.7 of
 *    the 291.7 uu/s the impulse added. The car is still reported grounded for
 *    the tick it takes off on, and jump.ts's JUMP_RESET_TIME_PAD is precisely
 *    the guard for that — it stops the still-grounded car from being handed a
 *    free second jump. Both halves are asserted in the tests.
 *
 * ===========================================================================
 * CAR CONTACT
 * ===========================================================================
 *
 * Step 3d is TWO things, and the distinction matters:
 *
 *   `stepCarArenaContacts` (../physics/car-arena) is the real one — the car's
 *   oriented hitbox against the arena mesh, depenetrated along the contact
 *   normal with full linear and angular impulse response. It is what stops a car
 *   flying sideways into a wall, and it never fires while the car is driving,
 *   because the suspension holds the body 18 uu clear of every surface it can
 *   drive on.
 *
 *   `containCar` is the BACKSTOP. It keeps the car's ORIGIN inside the analytic
 *   arena half-spaces, and exists only for the case the mesh pass structurally
 *   cannot catch: a car moving fast enough to pass through the shell inside one
 *   tick, whose box therefore never overlaps a triangle. At 2300 uu/s a tick is
 *   19 uu of travel against a box half-depth of 43, so it cannot happen with the
 *   speed cap in force — but "cannot happen" is not a guarantee, and a car
 *   leaving the arena is unrecoverable, so the half-spaces stay. It never
 *   engages during play; `test/sim/world.test.ts` keeps the escape guarantee.
 *
 * ===========================================================================
 * MUTATORS
 * ===========================================================================
 *
 * A world owns a `MutatorConfig` and the `ResolvedPhysics` it resolves to (see
 * ../physics/mutators). The config is STATE — it serializes, clones and travels
 * to a worker thread — while the resolved struct is derived, rebuilt from the
 * config wherever a world is built, and never serialized.
 *
 * The resolved struct is passed DOWN into the physics systems, one field read
 * at a time, and no system ever sees the enums. That is what keeps a mutated
 * world exactly as fast as a default one, and what makes the default path
 * bit-identical: with every row at `default` each field holds precisely the
 * constant the system used to import.
 *
 * GAME SPEED IS NOT APPLIED HERE, and cannot be. The constants are tuned for a
 * fixed 1/120 s tick and the model is not dt-invariant, so "half speed" is half
 * as many fixed ticks per real second, not a halved dt. `step` therefore knows
 * nothing about it; the pacing lives in `TickPacer` in ./loop, which is the
 * driver's problem. The enum is still carried in the world's config because a
 * match's settings belong with the match.
 *
 * ===========================================================================
 * DETERMINISM
 * ===========================================================================
 *
 * No `Math.random`, no wall clock, no `Date`, no iteration over a Map or Set
 * keyed by anything but insertion order. Randomness that the real game has —
 * which of the four respawn points a demolished car returns on — comes from
 * `World.rng`, a seeded generator that is part of the world's state and
 * snapshots with it. Every per-car field lives on the CarState itself, so a
 * world can be cloned, serialized and restored without losing anything (see
 * ./state). `test/sim/determinism.test.ts` runs 10,000 ticks of scripted random
 * input twice and compares the full serialized state.
 */

import { BALL_SPAWN, GOAL, GOAL_CENTER } from '../physics/arena-data';
import { soccarArena } from '../physics/arena';
import { integrateOrientation, stepAerialRotation } from '../physics/aerial';
import { applyBounce, integrateBall, makeBall, type BallState } from '../physics/ball';
import { BoostPadManager, stepBoost, type BoostPickup } from '../physics/boost';
import {
  applyCarBallHit,
  makePsyonixImpulseState,
  type PsyonixImpulseState,
} from '../physics/car-ball';
import {
  makeCarArenaScratch,
  stepCarArenaContacts,
  type CarArenaScratch,
} from '../physics/car-arena';
import { makeCar, neutralInput, type CarInput, type CarState } from '../physics/car-state';
import { ARENA, DT } from '../physics/constants';
import {
  DEFAULT_PHYSICS,
  normalizeMutators,
  resolvePhysics,
  type MutatorConfig,
  type ResolvedPhysics,
} from '../physics/mutators';
import {
  makeCarCarScratch,
  stepCarCarCollisions,
  type BumpEvent,
  type CarCarScratch,
} from '../physics/car-car';
import { stepRespawns, type DemoEvent } from '../physics/demo';
import { Rng } from '../physics/rng';
import { RESPAWN_SPAWNS } from '../physics/arena-data';
import {
  powerslideValue,
  stepGroundControl,
  type GroundStepOptions,
} from '../physics/ground';
import { stepJump } from '../physics/jump';
import { addScaledTo, clone, dot, length, vec3, type Vec3 } from '../physics/math';
import type { ArenaGeometry } from '../physics/surface';
import {
  DEFAULT_MATCH_CONFIG,
  MatchState,
  type GoalEvent,
  type MatchConfig,
  type MatchPhase,
} from '../game/match';
import { Kickoff } from './kickoff';

// ---------------------------------------------------------------------------
// Tuning that belongs to the integration layer, not to any physics module
// ---------------------------------------------------------------------------

/**
 * Passes of (depenetrate + bounce) per ball collision resolution.
 *
 * One pass is not enough in a corner: resolving against the floor moves the
 * ball into the wall, and the wall contact was measured at the old position.
 * Each pass re-queries, so the sequence is contracting and three passes settle
 * every seam in the soccar mesh — the ball-in-the-corner test runs 1200 ticks
 * against exactly that. It is a fixed count rather than a convergence loop so
 * that the tick cost is bounded and identical on every run.
 *
 * EXPORTED because src/bot/prediction.ts plans against this exact resolution.
 * It used to keep its own copy of the constant and of the loop below, which
 * meant a change here silently made every bot plan against a different game —
 * a failure with no symptom other than bots gradually missing more balls.
 */
export const BALL_CONTACT_PASSES = 3;

/** 1/sqrt(2), for the 45-degree corner planes. */
const INV_SQRT2 = Math.SQRT1_2;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** One car-ball contact resolved this tick. */
export interface BallTouch {
  /** Index into `World.cars`. */
  carIndex: number;
  team: 0 | 1;
  /** Contact point in world space — where a renderer should put the impact. */
  position: Vec3;
  /** |v_ball - v_car| before the hit, after the psyonix MAXDELTAVEL clamp. */
  relativeSpeed: number;
  /** False when this car's psyonix impulse was suppressed by its own cooldown. */
  psyonixApplied: boolean;
}

/**
 * Everything that happened in one tick, returned rather than polled so that
 * audio and VFX never have to re-derive an event by diffing state.
 */
export interface WorldStepEvents {
  /** Index of the tick that just ran. */
  tick: number;
  /** Match phase transition, or null when the phase did not change. */
  phase: MatchPhase | null;
  /** True when the tick was a frozen one and no physics ran. */
  frozen: boolean;
  /** True on the tick the field was reset to a kickoff. */
  kickoff: boolean;
  boostPickups: readonly BoostPickup[];
  demolitions: readonly DemoEvent[];
  /** Bumps applied this tick. A bump and a demolition are the two outcomes of
   *  the same contact, so these two lists are disjoint. */
  bumps: readonly BumpEvent[];
  /** Overlapping car pairs this tick, most of which are neither — a scrape. */
  carContacts: number;
  ballTouches: readonly BallTouch[];
  /** The goal scored this tick, or null. At most one per tick. */
  goal: GoalEvent | null;
}

const NO_EVENTS: readonly never[] = [];

export interface WorldOptions {
  /** Defaults to the memoized soccar mesh arena. */
  arena?: ArenaGeometry;
  /** Cars per team. Ignored when `cars` is given. Defaults to 3. */
  teamSize?: number;
  /** Explicit roster, for tests and for non-symmetric matches. */
  cars?: CarState[];
  matchConfig?: MatchConfig;
  /**
   * Seed for the world's PRNG (../physics/rng), which is part of the world's
   * state and decides everything the real game decides randomly. Defaults to 0,
   * so a world built with no options is still fully reproducible.
   */
  seed?: number;
  /**
   * The match's mutators (../physics/mutators). Defaults to every row at
   * `default`, which resolves to the shipped constants exactly — a world built
   * with no options is the unmutated game, to the bit.
   *
   * Note `gameSpeed` is carried but not read here: it is a property of the
   * DRIVER, not of the simulation. See the header of ../physics/mutators and
   * `TickPacer` in ./loop.
   */
  mutators?: MutatorConfig;
}

/** The last car to touch the ball, for goal attribution. */
export interface BallTouchRecord {
  carIndex: number;
  team: 0 | 1;
  tick: number;
}

// ---------------------------------------------------------------------------
// Ball vs arena
// ---------------------------------------------------------------------------

/**
 * Depenetrate and bounce a ball against the arena. Returns true when an actual
 * BOUNCE happened, as opposed to a depenetration on its own.
 *
 * Bounce is applied per contact, as ./ball's model requires, but only for a
 * contact the ball is actually moving INTO. Without that test a ball wedged in
 * a corner gets its velocity reversed once per surface and squirts out at
 * higher speed than it arrived — the classic multi-contact energy leak, and the
 * reason a naive resolver cannot keep a ball in a corner for 1200 ticks.
 *
 * A free function rather than a method because src/bot/prediction.ts has to run
 * the IDENTICAL resolution to stay bit-exact against a stepped world; see
 * `BALL_CONTACT_PASSES` above. The return value is unused by the world and
 * exists purely so the predictor can record its bounce ticks.
 *
 * `physics` carries the ball-size and bounciness mutators; a caller that omits
 * it gets the unmutated game, which is what every existing caller wants.
 */
export const resolveBallArena = (
  ball: BallState,
  arena: ArenaGeometry,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): boolean => {
  let bounced = false;
  for (let pass = 0; pass < BALL_CONTACT_PASSES; pass++) {
    const contacts = arena.collideSphere(ball.position, physics.ballRadius);
    if (contacts.length === 0) return bounced;

    let changed = false;
    for (const contact of contacts) {
      if (contact.depth > 0) {
        addScaledTo(ball.position, contact.normal, contact.depth);
        changed = true;
      }
      if (dot(ball.velocity, contact.normal) < 0) {
        applyBounce(ball, contact.normal, physics);
        changed = true;
        bounced = true;
      }
    }
    if (!changed) return bounced;
  }
  return bounced;
};

export class World {
  readonly arena: ArenaGeometry;
  readonly ball: BallState;
  readonly cars: readonly CarState[];
  readonly boostPads: BoostPadManager;
  readonly match: MatchState;
  readonly kickoff: Kickoff;
  /**
   * The match's mutator settings, exactly as the menu produced them. Part of the
   * state: it serializes, clones and travels to a worker with everything else,
   * so a restored world plays by the same rules. Never mutated after
   * construction — a mid-match mutator change is a new world.
   */
  readonly mutators: MutatorConfig;
  /**
   * `mutators` resolved into the numbers the tick actually reads. Resolved ONCE
   * here rather than per call: the hot path reads `physics.gravity`, never
   * `GRAVITY_SCALE[mutators.gravity]`, so a mutated world costs the same per
   * tick as an unmutated one.
   */
  readonly physics: ResolvedPhysics;
  /**
   * The world's seeded generator. Part of the state: it clones and serializes
   * with everything else, so a restored world makes the same "random" choices
   * the original was about to make.
   */
  readonly rng: Rng;

  /** Number of ticks simulated. Also the tick index handed to ./car-ball. */
  tick = 0;

  /**
   * Most recent car-ball contact. Survives across ticks because goal credit is
   * given to whoever touched the ball last, however long ago — a shot from the
   * far side of the field is still that player's goal.
   */
  lastTouch: BallTouchRecord | null = null;

  /** Psyonix-impulse cooldown, per car, parallel to `cars`. See ./car-ball §4. */
  private readonly impulseStates: PsyonixImpulseState[];

  /**
   * Per-car working buffers for the body-vs-arena pass. Allocated once, never
   * per tick: `stepCarArenaContacts` runs for every live car on every tick and
   * a fresh contact buffer each time was measurable in the benchmark.
   * NOT state — nothing here survives a tick, so it is neither cloned nor
   * serialized.
   */
  private readonly carArenaScratch: CarArenaScratch[];

  /** Working buffers for the car-vs-car pass. One for the whole field, not one
   *  per car: that pass is over PAIRS. Not state, same as `carArenaScratch`. */
  private readonly carCarScratch: CarCarScratch;

  /**
   * Kickoff ball position, and the goal line the ball must fully cross. Both
   * move with the ball-size mutator and neither changes during a match, so they
   * are resolved here rather than recomputed per kickoff and per tick. With a
   * default ball the offsets are exactly zero and both are the constants.
   */
  private readonly ballSpawn: Vec3;
  private readonly goalThresholdY: number;

  /**
   * The ownership flags handed to ./ground every tick, plus this world's
   * resolved physics. One object for the world's whole life: it used to be an
   * object literal built per car per tick, and the fields never vary.
   */
  private readonly groundOptions: GroundStepOptions;

  constructor(options: WorldOptions = {}) {
    this.arena = options.arena ?? soccarArena();
    this.match = new MatchState(options.matchConfig ?? DEFAULT_MATCH_CONFIG);
    this.mutators = normalizeMutators(options.mutators);
    this.physics = resolvePhysics(this.mutators);
    this.groundOptions = {
      applyBoostThrust: false, // conflict 1 — ./boost owns thrust
      updateSupersonicFlag: false, // conflict 2 — ./boost owns the flag
      integrateOrientationFlag: false, // conflict 3 — we own rotation
      physics: this.physics,
    };

    this.ballSpawn = vec3(BALL_SPAWN.x, BALL_SPAWN.y, this.physics.ballRestZ);
    this.goalThresholdY = GOAL.scoreBaseThresholdY + this.physics.ballRadius;

    this.ball = makeBall(clone(this.ballSpawn));
    this.rng = new Rng(options.seed ?? 0);

    const teamSize = options.teamSize ?? 3;
    this.cars =
      options.cars ??
      Array.from({ length: teamSize * 2 }, (_, i) => makeCar(i < teamSize ? 0 : 1));
    this.impulseStates = this.cars.map(() => makePsyonixImpulseState());
    this.carArenaScratch = this.cars.map(() => makeCarArenaScratch());
    this.carCarScratch = makeCarCarScratch(this.cars.length);

    this.boostPads = new BoostPadManager();
    this.kickoff = Kickoff.forCars(this.cars);

    this.resetForKickoff();
  }

  // -------------------------------------------------------------------------
  // Kickoff
  // -------------------------------------------------------------------------

  /**
   * Full kickoff reset: cars on their spawns with the mutator's spawn boost
   * (BOOST_SPAWN_AMOUNT unless the boost mutator says otherwise), ball at centre
   * field at rest, every boost pad back up, every per-car cooldown cleared.
   * Leaves the MATCH alone — the clock, the score and the phase belong to
   * MatchState and a kickoff does not change any of them.
   */
  resetForKickoff(): void {
    this.kickoff.place(this.cars, this.physics.boostSpawnAmount);
    this.holdKickoffPose();
    this.boostPads.reset();
    for (const state of this.impulseStates) {
      state.lastImpulseTick = Number.NEGATIVE_INFINITY;
    }
    this.lastTouch = null;
  }

  /**
   * Re-assert whatever pose the CURRENT frozen phase is meant to hold. Called
   * every frozen tick, which is why it must be idempotent and cheap.
   *
   * A countdown holds the kickoff pose. The goal celebration and the replay
   * hold the scoring pose, which needs no work at all: the frozen short-circuit
   * runs no physics, so nothing has moved since `freezeScoringPose` stopped the
   * field dead on the tick the ball crossed. Doing nothing is the correct
   * implementation of "hold where you are", and it is also the cheap one.
   */
  private holdFrozenPose(): void {
    if (this.match.phase === 'countdown') this.holdKickoffPose();
  }

  /**
   * Re-assert the kickoff pose without touching boost, pads or cooldowns.
   */
  private holdKickoffPose(): void {
    const seen: [number, number] = [0, 0];
    for (const car of this.cars) {
      const indexInTeam = seen[car.team]++;
      const spawn = this.kickoff.spawnFor(car.team, indexInTeam);
      car.position = clone(spawn.position);
      car.velocity = vec3(0, 0, 0);
      car.angularVelocity = vec3(0, 0, 0);
    }
    this.ball.position = clone(this.ballSpawn);
    this.ball.velocity = vec3(0, 0, 0);
    this.ball.angularVelocity = vec3(0, 0, 0);
  }

  // -------------------------------------------------------------------------
  // The tick
  // -------------------------------------------------------------------------

  /**
   * Advance the whole simulation by exactly one tick, in the order documented
   * at the top of this file.
   *
   * `inputs` is indexed in parallel with `cars`; a missing entry is a neutral
   * input, so a caller may drive only the cars it controls.
   */
  step(inputs: readonly CarInput[], dt: number = DT): WorldStepEvents {
    const tick = this.tick;
    const phase = this.match.step(dt);

    // A finished replay hands the field back for a fresh kickoff, and ONLY a
    // countdown does: entering the goal celebration used to reset here too,
    // which teleported the ball out of the net a frame after it went in.
    const kickoff = phase === 'countdown';
    if (kickoff) this.resetForKickoff();

    if (this.match.frozen) {
      this.holdFrozenPose();
      this.tick++;
      return {
        tick,
        phase,
        frozen: true,
        kickoff,
        boostPickups: NO_EVENTS,
        demolitions: NO_EVENTS,
        bumps: NO_EVENTS,
        carContacts: 0,
        ballTouches: NO_EVENTS,
        goal: null,
      };
    }

    const boostPickups = this.boostPads.step(this.cars, dt, this.physics);

    for (let i = 0; i < this.cars.length; i++) {
      const car = this.cars[i];
      if (car.demolished) continue;
      this.stepCar(car, inputs[i] ?? neutralInput(), dt, i);
    }

    // 4a — the spawn choice is drawn from the world's generator, and only on the
    // tick a car actually returns — see the note on `stepRespawns`.
    stepRespawns(this.cars, dt, (car) => this.rng.nextInt(RESPAWN_SPAWNS[car.team].length));

    // 4b — contact, bumps and demolitions in one pass, then the tunnelling
    // backstop again: depenetrating a pair moves cars the arena pass had placed.
    const { demolitions, bumps, contacts } = stepCarCarCollisions(
      this.cars,
      dt,
      this.carCarScratch
    );
    for (const car of this.cars) if (!car.demolished) containCar(car);

    const ballTouches = this.stepBall(dt);
    const goal = this.detectGoal();

    this.tick++;
    return {
      tick,
      phase,
      frozen: false,
      kickoff,
      boostPickups,
      demolitions,
      bumps,
      carContacts: contacts,
      ballTouches,
      goal,
    };
  }

  /** Is boost pad `index` (RLBot FieldInfo order) currently collectable? */
  padActive(index: number): boolean {
    const pad = this.boostPads.pads[index];
    return pad !== undefined && pad.active;
  }

  /**
   * Tick at which car `index` last fired its psyonix impulse, or
   * -Infinity for never. Part of the state (see ./state) and not derivable from
   * anything else, so the serializer needs a way in and out of it.
   */
  psyonixCooldownTick(index: number): number {
    const state = this.impulseStates[index];
    return state === undefined ? Number.NEGATIVE_INFINITY : state.lastImpulseTick;
  }

  setPsyonixCooldownTick(index: number, tick: number): void {
    const state = this.impulseStates[index];
    if (state !== undefined) state.lastImpulseTick = tick;
  }

  /** Analog powerslide value of car `index`, 0..1 — for tyre marks and audio. */
  powerslide(index: number): number {
    const car = this.cars[index];
    return car === undefined ? 0 : powerslideValue(car);
  }

  /** One live car: jump, ground control, the single rotation update, boost. */
  private stepCar(car: CarState, input: CarInput, dt: number, index: number): void {
    // 3a — reads last tick's ground contact, on purpose. See conflict 4.
    const jumpStep = stepJump(car, input, dt);

    // 3b — the only thing that moves a car. Every ownership flag is off; see
    // `groundOptions`, which also carries this world's resolved mutators.
    stepGroundControl(car, input, this.arena, dt, this.groundOptions);

    // 3c — exactly one orientation integration, whichever branch is taken.
    if (!car.onGround && jumpStep.airControl) {
      stepAerialRotation(car, jumpStep.airControlInput, dt);
    } else {
      integrateOrientation(car, dt);
    }

    // 3d — the real body contact, then the tunnelling backstop behind it.
    stepCarArenaContacts(car, this.arena, this.carArenaScratch[index]);
    containCar(car);

    // 3e — thrust, tank, and the supersonic flag on the final velocity.
    stepBoost(car, input, dt, this.physics);
  }

  // -------------------------------------------------------------------------
  // Ball
  // -------------------------------------------------------------------------

  /** Integrate the ball and resolve every contact it has this tick. */
  private stepBall(dt: number): BallTouch[] {
    integrateBall(this.ball, dt, this.physics);
    resolveBallArena(this.ball, this.arena, this.physics);
    const touches = this.resolveBallCars();
    // A car can push the ball into the shell; re-resolve so it never ends a
    // tick overlapping a wall with an outward velocity.
    if (touches.length > 0) resolveBallArena(this.ball, this.arena, this.physics);
    return touches;
  }

  /**
   * Car-ball contacts, in car order. Every overlapping car gets its full hit —
   * ./car-ball §4 is explicit that the psyonix cooldown is per car, so both
   * cars in a pinch fire on the same tick.
   *
   * The ball is pushed out of each hitbox as it is resolved. The CAR is not
   * moved and gets no reaction: that is the model, not an omission.
   */
  private resolveBallCars(): BallTouch[] {
    const touches: BallTouch[] = [];
    for (let i = 0; i < this.cars.length; i++) {
      const car = this.cars[i];
      if (car.demolished) continue;

      const hit = applyCarBallHit(car, this.ball, this.impulseStates[i], this.tick, this.physics);
      if (!hit) continue;

      addScaledTo(this.ball.position, hit.contact.normal, hit.contact.depth);
      this.lastTouch = { carIndex: i, team: car.team, tick: this.tick };
      touches.push({
        carIndex: i,
        team: car.team,
        position: clone(hit.contact.point),
        relativeSpeed: hit.relativeSpeed,
        psyonixApplied: hit.psyonixApplied,
      });
    }
    return touches;
  }

  // -------------------------------------------------------------------------
  // Goals
  // -------------------------------------------------------------------------

  /**
   * Score a goal if the ball has fully crossed a goal line.
   *
   * The threshold is `this.goalThresholdY` = the goal-line plane
   * (GOAL.scoreBaseThresholdY = 5124.25) plus the ball's radius, i.e. the
   * shipped 5215.5 with a default ball. The whole ball must be past the line —
   * that IS the "account for the ball radius" step, and it is the same test
   * RocketSim's `IsBallScored` makes. A ball-size mutator moves the threshold
   * with the ball, which is the only way a gigantic ball can still be judged by
   * its trailing edge. No x or z bound is needed: the back wall is solid
   * everywhere except the mouth, so the only way past 5124.25 is through a goal.
   *
   * WHICH TEAM. Blue is team 0 and DEFENDS -Y (arena-data.GOAL_CENTER[0] is at
   * -Y). A ball entering the goal at -Y is therefore a goal FOR orange, team 1.
   * Positive y scores for blue. Getting this backwards is invisible until
   * someone reads the scoreboard, so it is asserted directly in the tests.
   *
   * Credit goes to whoever touched the ball last; if that player defends the
   * goal the ball went into, it is an own goal and the scorer is null.
   */
  private detectGoal(): GoalEvent | null {
    const y = this.ball.position.y;
    if (Math.abs(y) <= this.goalThresholdY) return null;

    const team: 0 | 1 = y > 0 ? 0 : 1;
    const touch = this.lastTouch;
    const event: GoalEvent = {
      team,
      scorer: touch && touch.team === team ? touch.carIndex : null,
      clockRemaining: this.match.clock,
      speed: length(this.ball.velocity),
    };

    // MatchState refuses a goal outside live play, which is also what stops a
    // ball sitting in the net from scoring again every tick.
    if (this.match.scoreGoal(event) === null) return null;
    this.freezeScoringPose();
    return event;
  }

  /**
   * Stop the field dead exactly where the goal left it.
   *
   * Positions and orientations are deliberately untouched — those ARE the
   * scoring pose, and the celebration and the replay are both staged against
   * them. Only the velocities are cleared, and they are cleared because the
   * frozen phases that follow last nine seconds: a car left holding 2000 uu/s
   * would spin its wheels and stream a supersonic trail for all of it, which is
   * a freeze frame that visibly is not frozen.
   *
   * Runs after the GoalEvent has been built, so `event.speed` is still the
   * speed the ball actually crossed at.
   */
  private freezeScoringPose(): void {
    for (const car of this.cars) {
      car.velocity = vec3(0, 0, 0);
      car.angularVelocity = vec3(0, 0, 0);
      car.supersonic = false;
      car.supersonicTimer = 0;
    }
    this.ball.velocity = vec3(0, 0, 0);
    this.ball.angularVelocity = vec3(0, 0, 0);
  }

  /**
   * Cut the goal celebration or the replay short, at the player's request. See
   * `MatchState.skipReplay` for why the phase it lands in is not always a
   * countdown. Returns the phase entered, or null when there was nothing to
   * skip.
   *
   * The kickoff reset is done HERE rather than deferred to the next `step`, so
   * that "the match is in countdown" and "the field is on its spawns" become
   * true at the same instant however the replay ended.
   */
  skipReplay(): MatchPhase | null {
    const phase = this.match.skipReplay();
    if (phase === 'countdown') this.resetForKickoff();
    return phase;
  }
}

// ---------------------------------------------------------------------------
// Car containment — the tunnelling backstop
// ---------------------------------------------------------------------------

/**
 * Keep a car's origin inside the analytic arena. See "CAR CONTACT" at the top
 * of this file: the real contact is `stepCarArenaContacts`, and this runs behind
 * it purely so that no sequence of events can leave a car outside the shell.
 *
 * It is measurably inert during play — the body pass keeps the car's ORIGIN at
 * least a box half-extent from every one of these planes, so none of them is
 * ever violated. Keeping it costs six comparisons per car per tick and removes
 * an entire class of unrecoverable failure.
 *
 * Each constraint is a half-space with an inward unit normal; a violated one is
 * resolved by the minimal translation along that normal, and the inward-facing
 * velocity component is removed (never reversed — cars scrape, they do not
 * bounce). The constraints are applied in a fixed order, so the result is
 * deterministic even where two of them overlap.
 */
export const containCar = (car: CarState): void => {
  const p = car.position;
  const v = car.velocity;

  /** Resolve `dot(p - plane, n) >= 0` for an inward unit normal `n`. */
  const push = (nx: number, ny: number, nz: number, distance: number): void => {
    if (distance >= 0) return;
    p.x -= nx * distance;
    p.y -= ny * distance;
    p.z -= nz * distance;
    const vn = v.x * nx + v.y * ny + v.z * nz;
    if (vn < 0) {
      v.x -= nx * vn;
      v.y -= ny * vn;
      v.z -= nz * vn;
    }
  };

  const sx = p.x >= 0 ? 1 : -1;
  const sy = p.y >= 0 ? 1 : -1;

  push(0, 0, 1, p.z - ARENA.floor);
  push(0, 0, -1, ARENA.ceiling - p.z);
  push(-sx, 0, 0, ARENA.halfWidth - Math.abs(p.x));

  // The back wall is open across the goal mouth: inside it the car may run all
  // the way to the back of the net.
  const inGoalMouth = Math.abs(p.x) <= GOAL.centerToPostMesh && p.z <= GOAL.heightMesh;
  const backLimit = inGoalMouth ? GOAL.backNetY : ARENA.halfLength;
  push(0, -sy, 0, backLimit - Math.abs(p.y));

  // 45-degree corner planes, |x| + |y| <= 8064. The plane distance is the axis
  // slack divided by sqrt(2).
  push(
    -sx * INV_SQRT2,
    -sy * INV_SQRT2,
    0,
    (ARENA.cornerPlaneIntercept - Math.abs(p.x) - Math.abs(p.y)) * INV_SQRT2
  );
};

// ---------------------------------------------------------------------------
// Helpers for callers and tests
// ---------------------------------------------------------------------------

/**
 * True when a point is inside the playable volume OR inside a goal. The ball is
 * legitimately outside `ArenaGeometry.contains` while it is in the net, so
 * "did the ball escape?" cannot be asked of `contains` alone.
 */
export const insideArenaOrGoal = (p: Vec3, tolerance = 1e-6): boolean => {
  if (p.z < ARENA.floor - tolerance || p.z > ARENA.ceiling + tolerance) return false;
  if (Math.abs(p.x) > ARENA.halfWidth + tolerance) return false;
  if (Math.abs(p.x) + Math.abs(p.y) > ARENA.cornerPlaneIntercept + tolerance) return false;
  if (Math.abs(p.y) <= ARENA.halfLength + tolerance) return true;

  // Past the goal line: only the goal interior is legal.
  return (
    Math.abs(p.y) <= GOAL.backNetY + tolerance &&
    Math.abs(p.x) <= GOAL.centerToPostMesh + tolerance &&
    p.z <= GOAL.heightMesh + tolerance
  );
};

/** Centre of the goal `team` is attacking. Blue (0) defends -Y, so it attacks +Y. */
export const attackingGoalCenter = (team: 0 | 1): Vec3 => clone(GOAL_CENTER[team === 0 ? 1 : 0]);
